import type { CSSProperties, ReactNode } from "react";

/**
 * Tabela de dados (Design.md §9): cabeçalho `--panel-alt`, rótulos de coluna
 * 10,5–11px/700 com letter-spacing, linhas com hover. Genérica — cada coluna
 * declara como renderiza a célula.
 */
export type CellAlign = "left" | "right" | "center";

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  width?: number | string;
  align?: CellAlign;
}

export interface DataTableProps<T> {
  columns: readonly Column<T>[];
  rows: readonly T[];
  getRowKey: (row: T) => string;
  empty?: ReactNode;
  style?: CSSProperties;
}

export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  empty = "Nenhum registro encontrado.",
  style
}: DataTableProps<T>): ReactNode {
  return (
    <div style={{ overflowX: "auto", ...(style ?? {}) }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 12.5
        }}
      >
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                style={{
                  textAlign: col.align ?? "left",
                  width: col.width,
                  background: "var(--panel-alt)",
                  color: "var(--text-muted)",
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  padding: "0.7rem 0.8rem",
                  borderBottom: "1px solid var(--border)",
                  whiteSpace: "nowrap"
                }}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                style={{
                  padding: "1.25rem 0.8rem",
                  textAlign: "center",
                  color: "var(--text-muted)"
                }}
              >
                {empty}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={getRowKey(row)} className="ficc-hover-row">
                {columns.map((col) => (
                  <td
                    key={col.key}
                    style={{
                      textAlign: col.align ?? "left",
                      padding: "0.75rem 0.8rem",
                      borderBottom: "1px solid var(--border-soft)",
                      verticalAlign: "middle"
                    }}
                  >
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
